import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useEffect, useState } from "react";
import { VerifyIdentityForm } from "@/components/forms/VerifyIdentity";
import { useVerifyIdentity2FA, Verify2FAStep } from "@/hooks/user/useVerify2FA";
import { t } from "i18next";
import { useMultiModalController } from "@/store/multiModalController.store";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  VerifyIdentitySchema,
  type VerifyIdentitySchemaType,
} from "@/schemas/verifyIdentity.schema";

interface Props {
  trigger: React.ReactNode;
  name: string;
}

export function VerifyIdentity2FADialog(props: Props): JSX.Element {
  const { step, handleVerify, resetVerifyState, error, loading } =
    useVerifyIdentity2FA();
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const { setOpenByModalId } = useMultiModalController();

  const form = useForm<VerifyIdentitySchemaType>({
    resolver: zodResolver(VerifyIdentitySchema),
    defaultValues: {
      otp: "",
      password: "",
    },
  });

  useEffect(() => {
    if (!openDialog) {
      resetVerifyState();
    }
  }, [openDialog, resetVerifyState]);

  useEffect(() => {
    if (step == Verify2FAStep.COMPLETED) {
      setOpenDialog(false);
      setOpenByModalId(props.name, true);
    }
  }, [step, setOpenByModalId, props.name]);

  return (
    <div className="space-y-0">
      <Dialog modal={false} open={openDialog} onOpenChange={setOpenDialog}>
        {openDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-80 z-40" />
        )}
        <DialogTrigger asChild>{props.trigger}</DialogTrigger>
        <DialogContent>
          <DialogHeader className="space-y-4">
            <DialogTitle>
              {t("components.verifyIdentity2FADialog.title")}
            </DialogTitle>
            <DialogDescription>
              {t("components.verifyIdentity2FADialog.description")}
            </DialogDescription>
          </DialogHeader>
          <VerifyIdentityForm
            error={error}
            form={form}
            loading={loading}
            step={step}
            onCancel={() => {
              setOpenDialog(false);
            }}
            onSubmit={(data) => {
              void handleVerify(data.password, data.otp);
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
